/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useCallback, Ref } from 'preact/hooks';
import { TableColumn } from '../Table';
import { updateStickyEdges } from '../utils/TableScrollUtils';

type UseScrollHandlingProps<K, D, C extends string> = {
  outerTableRef: Ref<HTMLDivElement>;
  columnsArray: TableColumn<K, D, C>[];
  stickyEdgesState: { start?: number; end?: number };
  setStickyEdgesState: (stickyEdgesState: { start?: number; end?: number }) => void;
  fullColumnWidths?: number[];
};

/**
 * Hook that manages handling scroll events on the Preact Table
 */
export function useScrollHandling<K extends string | number, D, C extends string>({
  outerTableRef,
  columnsArray,
  stickyEdgesState,
  setStickyEdgesState,
  fullColumnWidths
}: UseScrollHandlingProps<K, D, C>) {
  // Scroll hander for the outer Table. It ensures the Table's sticky edges are updated.
  const _scrollHandler = useCallback(() => {
    if (outerTableRef.current != null && fullColumnWidths != null) {
      updateStickyEdges(
        outerTableRef.current,
        columnsArray,
        fullColumnWidths,
        stickyEdgesState,
        setStickyEdgesState,
        outerTableRef.current.scrollLeft
      );
    }
  }, [outerTableRef, columnsArray, stickyEdgesState, setStickyEdgesState, fullColumnWidths]);

  return { onScroll: _scrollHandler };
}
